import { useEffect, useState } from "react";
import { Check, FileXls, UploadSimple, UserCheck, WhatsappLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";

export default function ParentPortalTab() {
    const [parents, setParents] = useState([]);
    const [requests, setRequests] = useState([]);
    const [file, setFile] = useState(null);
    const [report, setReport] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [passwords, setPasswords] = useState({});
    const [lastReset, setLastReset] = useState(null);
    const [imported, setImported] = useState([]);
    const [activateTarget, setActivateTarget] = useState(null);
    const [activateForm, setActivateForm] = useState({ username: "", password: "", photo_url: "" });
    const [activating, setActivating] = useState(false);
    const [activateResult, setActivateResult] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);

    const loadImported = () => {
        api.get("/admin/imported-records")
            .then((r) => setImported(r.data))
            .catch(() => {});
    };

    const openActivate = (record) => {
        setActivateTarget(record);
        setActivateForm({ username: record.parent_username || "", password: "", photo_url: record.photo_url || "" });
        setActivateResult(null);
    };

    const uploadActivatePhoto = async (photoFile) => {
        if (!photoFile) return;
        setPhotoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", photoFile);
            const { data } = await api.post("/admin/upload", fd);
            setActivateForm((prev) => ({ ...prev, photo_url: data.url }));
            toast.success("Foto terunggah");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setPhotoUploading(false);
        }
    };

    const submitActivate = async () => {
        if (!activateForm.username.trim()) {
            toast.error("Username wajib diisi");
            return;
        }
        setActivating(true);
        try {
            const { data } = await api.post(`/admin/imported-records/${activateTarget.id}/activate`, activateForm);
            setActivateResult(data);
            toast.success(data.message);
            loadImported();
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setActivating(false);
        }
    };

    const load = async () => {
        try {
            const [parentsRes, requestsRes] = await Promise.all([
                api.get("/admin/parents"),
                api.get("/admin/password-reset-requests"),
            ]);
            setParents(parentsRes.data);
            setRequests(requestsRes.data);
        } catch {
            toast.error("Gagal memuat data portal orang tua");
        }
    };

    useEffect(() => {
        load();
        loadImported();
    }, []);

    const importFile = async () => {
        if (!file) {
            toast.error("Pilih file Excel terlebih dahulu");
            return;
        }
        setUploading(true);
        setReport(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/student-import", fd);
            setReport(data);
            toast.success("Import data siswa selesai");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const approve = async (request) => {
        try {
            const { data } = await api.post(`/admin/password-reset-requests/${request.id}/approve`, {
                new_password: passwords[request.id] || "",
            });
            setLastReset({ ...data, parent_phone: request.parent_phone });
            toast.success("Password berhasil direset");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const reject = async (request) => {
        const reason = window.prompt("Alasan penolakan (opsional):", "") || "";
        try {
            await api.post(`/admin/password-reset-requests/${request.id}/reject`, { reason });
            toast.success("Permintaan ditolak");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const parentWaLink = (item) => {
        if (!item.parent_phone) return null;
        let phone = String(item.parent_phone).replace(/\D/g, "");
        if (phone.startsWith("0")) phone = `62${phone.slice(1)}`;
        const text = encodeURIComponent(`Halo, reset password Portal Orang Tua Mandalika Inlineskate telah disetujui.\n\nUsername: ${item.username}\nPassword baru: ${item.new_password}\n\nSilakan login lalu simpan password ini baik-baik.`);
        return `https://wa.me/${phone}?text=${text}`;
    };

    return (
        <div className="space-y-12" data-testid="parent-portal-tab">
            <section>
                <h2 className="font-display uppercase text-2xl text-neutral-900 mb-3">Import Data Siswa</h2>
                <p className="text-sm text-racing-muted mb-6 max-w-3xl">
                    Upload file .xlsx data siswa. Sistem membuat username dari Email, atau WhatsApp orang tua jika email kosong. Password awal memakai WhatsApp Ayah/Wali, lalu Ibu/Wali, lalu siswa.
                </p>
                <div className="border border-racing-line bg-white p-6 flex flex-col md:flex-row gap-4 md:items-center">
                    <label className="flex-1 flex items-center gap-3 border border-dashed border-racing-line bg-racing-surface px-4 py-4 cursor-pointer" data-testid="student-import-file-label">
                        <FileXls size={24} className="text-racing-gold" />
                        <span className="text-sm text-racing-muted">{file ? file.name : "Pilih file Data Siswa (.xlsx)"}</span>
                        <input
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            data-testid="student-import-file-input"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </label>
                    <button
                        onClick={importFile}
                        disabled={uploading}
                        data-testid="student-import-submit"
                        className="flex items-center justify-center gap-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.2em] px-6 py-4 transition-colors"
                    >
                        <UploadSimple size={16} /> {uploading ? "Mengimpor..." : "Import Data"}
                    </button>
                </div>

                {report && (
                    <div className="mt-5 border border-racing-line bg-white p-6" data-testid="student-import-report">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-racing-line border border-racing-line mb-5">
                            <div className="bg-racing-surface p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Baris</p><p className="font-display text-3xl">{report.total_rows}</p></div>
                            <div className="bg-racing-surface p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Data Siswa</p><p className="font-display text-3xl">{report.imported_records}</p></div>
                            <div className="bg-racing-surface p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Akun Baru</p><p className="font-display text-3xl text-racing-red">{report.created_accounts}</p></div>
                            <div className="bg-racing-surface p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Dilewati</p><p className="font-display text-3xl">{report.skipped.length}</p></div>
                        </div>
                        {report.skipped.length > 0 && (
                            <div className="mb-4">
                                <p className="text-xs uppercase tracking-[0.2em] text-racing-red mb-2">Perlu dicek</p>
                                <ul className="space-y-1 text-sm text-racing-muted max-h-36 overflow-y-auto">
                                    {report.skipped.map((item, i) => <li key={i}>{item.sheet} baris {item.row}: {item.name} — {item.reason}</li>)}
                                </ul>
                            </div>
                        )}
                        {report.warnings.length > 0 && (
                            <p className="text-xs text-racing-muted">{report.warnings.length} penyesuaian akun keluarga ditambahkan.</p>
                        )}
                    </div>
                )}
            </section>

            <section>
                <div className="flex items-center justify-between gap-4 mb-6">
                    <h2 className="font-display uppercase text-2xl text-neutral-900">Persetujuan Lupa Password</h2>
                    <span className="text-xs uppercase tracking-[0.2em] text-racing-muted" data-testid="reset-request-count">{requests.filter((r) => r.status === "pending").length} pending</span>
                </div>
                {lastReset && (
                    <div className="mb-5 border border-racing-gold bg-racing-gold/10 p-5" data-testid="reset-password-result">
                        <p className="text-sm text-neutral-900 mb-2">Password baru untuk <strong>{lastReset.username}</strong>: <strong className="text-racing-red">{lastReset.new_password}</strong></p>
                        {parentWaLink(lastReset) && (
                            <a href={parentWaLink(lastReset)} target="_blank" rel="noopener noreferrer" data-testid="send-reset-wa-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#128C7E]">
                                <WhatsappLogo size={16} weight="fill" /> Kirim ke orang tua via WA
                            </a>
                        )}
                    </div>
                )}
                <div className="border border-racing-line overflow-x-auto">
                    <table className="w-full text-sm min-w-[760px]">
                        <thead>
                            <tr className="border-b border-racing-line bg-racing-surface">
                                {["Username", "Nama Siswa", "Status", "Tanggal", "Password Baru", "Aksi"].map((h) => (
                                    <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted font-medium">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 && (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-racing-muted" data-testid="reset-requests-empty">Belum ada permintaan reset password.</td></tr>
                            )}
                            {requests.map((request, i) => (
                                <tr key={request.id} className="border-b border-racing-line last:border-0" data-testid={`reset-request-row-${i}`}>
                                    <td className="px-4 py-3 text-neutral-900 break-all">{request.username}</td>
                                    <td className="px-4 py-3 text-neutral-800">{request.student_name || "—"}</td>
                                    <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.2em] ${request.status === "pending" ? "text-racing-golddeep" : request.status === "approved" ? "text-green-700" : "text-racing-red"}`}>{request.status}</span></td>
                                    <td className="px-4 py-3 text-xs text-racing-muted">{(request.created_at || "").slice(0, 10)}</td>
                                    <td className="px-4 py-3">
                                        {request.status === "pending" ? (
                                            <input
                                                type="text"
                                                value={passwords[request.id] || ""}
                                                onChange={(e) => setPasswords({ ...passwords, [request.id]: e.target.value })}
                                                placeholder="Kosongkan untuk otomatis"
                                                data-testid={`reset-password-input-${i}`}
                                                className="w-44 bg-racing-surface border border-racing-line px-3 py-2 text-xs focus:outline-none focus:border-racing-red"
                                            />
                                        ) : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        {request.status === "pending" && (
                                            <div className="flex gap-2">
                                                <button onClick={() => approve(request)} data-testid={`reset-approve-${i}`} className="p-2 border border-racing-line text-green-700 hover:bg-green-700 hover:text-white transition-colors" aria-label="Setujui"><Check size={16} /></button>
                                                <button onClick={() => reject(request)} data-testid={`reset-reject-${i}`} className="p-2 border border-racing-line text-racing-red hover:bg-racing-red hover:text-white transition-colors" aria-label="Tolak"><X size={16} /></button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section>
                <h2 className="font-display uppercase text-2xl text-neutral-900 mb-6">Akun Orang Tua</h2>
                <div className="border border-racing-line overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead>
                            <tr className="border-b border-racing-line bg-racing-surface">
                                {["Username", "Nama Akun", "Kontak", "Jumlah Anak", "Data Anak"].map((h) => (
                                    <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted font-medium">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {parents.length === 0 && (
                                <tr><td colSpan="5" className="px-4 py-8 text-center text-racing-muted" data-testid="parents-empty">Belum ada akun orang tua. Import file data siswa terlebih dahulu.</td></tr>
                            )}
                            {parents.map((parent, i) => (
                                <tr key={parent.id} className="border-b border-racing-line last:border-0 hover:bg-black/[0.03]" data-testid={`parent-account-row-${i}`}>
                                    <td className="px-4 py-3 text-neutral-900 break-all">{parent.username || parent.email}</td>
                                    <td className="px-4 py-3 text-neutral-800">{parent.name}</td>
                                    <td className="px-4 py-3 text-neutral-800">{parent.contact_phone || "—"}</td>
                                    <td className="px-4 py-3 text-neutral-900 font-medium">{parent.child_count}</td>
                                    <td className="px-4 py-3 text-racing-muted max-w-[320px] truncate">{(parent.children || []).map((c) => c.name).join(", ") || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section data-testid="import-activation-section">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display uppercase text-lg text-neutral-900 flex items-center gap-2"><UserCheck size={20} className="text-racing-red" /> Aktivasi Data Excel</h3>
                    <span className="text-xs text-racing-muted" data-testid="imported-records-count">{imported.length} data</span>
                </div>
                <p className="text-xs text-racing-muted mb-4">Ubah data hasil import Excel menjadi siswa aktif sekaligus atlet publik. Saat aktivasi Anda bisa langsung menambahkan foto dan mengatur username serta password akun orang tuanya.</p>
                {imported.length === 0 ? (
                    <p className="text-sm text-racing-muted border border-racing-line bg-white p-5" data-testid="imported-empty">Belum ada data hasil import Excel.</p>
                ) : (
                    <div className="overflow-x-auto border border-racing-line bg-white">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-racing-line bg-racing-surface">
                                    {["Nama", "Kelas", "Sheet", "Akun", "Status"].map((h) => <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] text-racing-muted">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {imported.map((record, i) => (
                                    <tr key={record.id} className="border-b border-racing-line last:border-0 hover:bg-black/[0.03]" data-testid={`imported-row-${i}`}>
                                        <td className="px-4 py-3 text-neutral-900 font-medium">{record.name}</td>
                                        <td className="px-4 py-3 text-neutral-800">{record.class_name || "—"}</td>
                                        <td className="px-4 py-3 text-racing-muted">{record.source_sheet || "—"}</td>
                                        <td className="px-4 py-3 text-racing-muted break-all">{record.has_account ? (record.parent_username || "ada") : "belum ada"}</td>
                                        <td className="px-4 py-3">
                                            {record.activated ? (
                                                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-green-600/50 bg-green-50 text-green-700" data-testid={`imported-activated-${i}`}><Check size={12} weight="bold" /> Aktif</span>
                                            ) : (
                                                <button type="button" onClick={() => openActivate(record)} data-testid={`imported-activate-${i}`} className="bg-racing-red hover:bg-racing-redhover text-white text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-colors">Aktifkan</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {activateTarget && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="activate-dialog">
                    <div className="bg-white border border-racing-line max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        {activateResult ? (
                            <div data-testid="activate-success">
                                <h3 className="font-display uppercase text-xl text-green-700 flex items-center gap-2"><Check size={20} weight="bold" /> Aktivasi Berhasil</h3>
                                <div className="mt-4 space-y-2 text-sm border border-racing-line bg-racing-surface p-4">
                                    <p><span className="text-racing-muted">Atlet:</span> <span className="font-medium">{activateTarget.name}</span></p>
                                    <p><span className="text-racing-muted">NIA:</span> <span className="font-display text-racing-red">{activateResult.nia}</span></p>
                                    <p><span className="text-racing-muted">Username:</span> <span className="font-medium break-all">{activateResult.username}</span></p>
                                    {activateForm.password && <p><span className="text-racing-muted">Password:</span> <span className="font-medium break-all">{activateForm.password}</span></p>}
                                </div>
                                {activateResult.parent_phone && (
                                    <a
                                        href={`https://wa.me/${activateResult.parent_phone}?text=${encodeURIComponent(`Halo, akun Portal Orang Tua Mandalika Inlineskate untuk ananda ${activateTarget.name} sudah aktif.\n\nUsername: ${activateResult.username}\nPassword: ${activateForm.password || "(password lama tetap berlaku)"}\n\nLogin di website klub, menu Portal Orang Tua.`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="activate-whatsapp-link"
                                        className="mt-4 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs uppercase tracking-[0.2em] py-3 transition-colors"
                                    >
                                        <WhatsappLogo size={16} weight="bold" /> Kirim Info Login via WhatsApp
                                    </a>
                                )}
                                <button onClick={() => { setActivateTarget(null); setActivateResult(null); }} data-testid="activate-done" className="mt-3 w-full border border-racing-line text-racing-muted text-xs uppercase tracking-[0.2em] py-3">Selesai</button>
                            </div>
                        ) : (
                            <div>
                                <h3 className="font-display uppercase text-xl">Aktifkan Data Excel</h3>
                                <p className="text-sm text-racing-muted mt-1">{activateTarget.name} • {activateTarget.class_name || "-"}</p>
                                <div className="space-y-4 mt-5">
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Atlet (opsional)</label>
                                        {activateForm.photo_url ? (
                                            <div className="flex items-center gap-3">
                                                <img src={fileUrl(activateForm.photo_url)} alt="Foto atlet" className="w-16 h-16 object-cover border border-racing-line" data-testid="activate-photo-preview" />
                                                <button type="button" onClick={() => setActivateForm({ ...activateForm, photo_url: "" })} className="text-[10px] uppercase tracking-[0.2em] text-racing-red">Hapus</button>
                                            </div>
                                        ) : (
                                            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-racing-line hover:border-racing-red p-4 cursor-pointer text-[10px] uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors" data-testid="activate-photo-dropzone">
                                                {photoUploading ? "Mengunggah..." : <><UploadSimple size={16} /> Pilih Foto</>}
                                                <input type="file" accept="image/*" className="hidden" data-testid="activate-photo-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadActivatePhoto(f); }} />
                                            </label>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Username Akun Orang Tua</label>
                                        <input type="text" value={activateForm.username} onChange={(e) => setActivateForm({ ...activateForm, username: e.target.value })} data-testid="activate-username-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" />
                                        <p className="mt-1.5 text-xs text-racing-muted">Tanpa spasi, minimal 3 karakter.</p>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Password {activateTarget.has_account ? "Baru (kosongkan jika tidak diganti)" : "Akun (wajib, min. 6 karakter)"}</label>
                                        <input type="text" value={activateForm.password} onChange={(e) => setActivateForm({ ...activateForm, password: e.target.value })} data-testid="activate-password-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder={activateTarget.has_account ? "••••••" : "buat password awal"} />
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                    <button onClick={submitActivate} disabled={activating || photoUploading} data-testid="activate-submit" className="flex-1 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] py-3.5 transition-colors">
                                        {activating ? "Mengaktifkan..." : "Aktifkan Sekarang"}
                                    </button>
                                    <button onClick={() => setActivateTarget(null)} data-testid="activate-cancel" className="flex-1 border border-racing-line text-racing-muted text-xs uppercase tracking-[0.2em] py-3.5">Batal</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
